import React, { useEffect, useRef } from 'react';

interface LoadingScreenProps {
    progress: number;
}

const bootLines = [
    'mounting /profile',
    'indexing academic papers',
    'warming portfolio windows',
    'loading award certificates',
];

const butterflyPalette = {
    outline: '#071620',
    body: '#143A56',
    dark: '#2E76AA',
    mid: '#66BDEB',
    light: '#BDEEFF',
};

const drawPx = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    color: string
) => {
    ctx.fillStyle = color;
    ctx.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
};

const drawLoadingButterfly = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    cell = 6,
    frame = 0
) => {
    const lift = frame % 2 === 0 ? 0 : -1;
    const totalCols = 42;
    const r = (gx: number, gy: number, gw: number, gh: number, color: string) => {
        drawPx(ctx, x + gx * cell, y + gy * cell, gw * cell, gh * cell, color);
    };

    const drawSpanBothSides = (
        spans: Array<[number, number, number]>,
        color: string
    ) => {
        for (const [gy, start, end] of spans) {
            const yy = gy + (gy < 12 ? lift : 0);
            r(start, yy, end - start + 1, 1, color);
            r(totalCols - end - 1, yy, end - start + 1, 1, color);
        }
    };

    const wingOutline: Array<[number, number, number]> = [
        [2, 12, 16],
        [3, 9, 18],
        [4, 7, 19],
        [5, 5, 20],
        [6, 3, 20],
        [7, 2, 20],
        [8, 1, 19],
        [9, 1, 19],
        [10, 2, 18],
        [11, 3, 17],
        [12, 4, 17],
        [13, 6, 18],
        [14, 8, 19],
        [15, 5, 19],
        [16, 3, 18],
        [17, 2, 17],
        [18, 2, 16],
        [19, 3, 15],
        [20, 4, 14],
        [21, 5, 13],
        [22, 7, 12],
        [23, 9, 11],
    ];
    const wingMain: Array<[number, number, number]> = [
        [3, 12, 15],
        [4, 9, 17],
        [5, 7, 18],
        [6, 5, 18],
        [7, 3, 18],
        [8, 3, 17],
        [9, 3, 17],
        [10, 4, 16],
        [11, 5, 16],
        [12, 6, 16],
        [13, 8, 17],
        [14, 10, 18],
        [15, 7, 18],
        [16, 5, 17],
        [17, 4, 16],
        [18, 4, 15],
        [19, 5, 14],
        [20, 6, 13],
        [21, 8, 12],
    ];
    const wingShadow: Array<[number, number, number]> = [
        [14, 15, 18],
        [15, 7, 10],
        [16, 5, 8],
        [18, 4, 7],
        [20, 6, 9],
    ];
    const wingHighlight: Array<[number, number, number]> = [
        [6, 8, 10],
        [7, 8, 9],
        [9, 11, 14],
        [10, 12, 15],
        [18, 9, 11],
        [19, 9, 10],
    ];

    drawSpanBothSides(wingOutline, butterflyPalette.outline);
    drawSpanBothSides(wingMain, butterflyPalette.mid);
    drawSpanBothSides(wingShadow, butterflyPalette.dark);
    drawSpanBothSides(wingHighlight, butterflyPalette.light);

    r(19, 3, 4, 22, butterflyPalette.outline);
    r(20, 6, 2, 18, butterflyPalette.body);
    r(19, 10, 4, 2, butterflyPalette.dark);
    r(19, 17, 4, 2, butterflyPalette.dark);
    r(19, 1, 1, 3, butterflyPalette.outline);
    r(22, 1, 1, 3, butterflyPalette.outline);
    r(16, 0, 3, 1, butterflyPalette.outline);
    r(23, 0, 3, 1, butterflyPalette.outline);
};

const LoadingButterfly: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = 280;
        canvas.height = 250;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.imageSmoothingEnabled = false;
        let raf = 0;
        const render = (time: number) => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.imageSmoothingEnabled = false;
            drawLoadingButterfly(ctx, 16, 42, 6, Math.floor(time / 520) % 2);
            raf = window.requestAnimationFrame(render);
        };
        raf = window.requestAnimationFrame(render);
        return () => window.cancelAnimationFrame(raf);
    }, []);

    return <canvas ref={canvasRef} className="loading-butterfly-canvas" aria-hidden="true" />;
};

const LoadingScreen: React.FC<LoadingScreenProps> = ({ progress }) => {
    const pct = Math.round(progress * 100);
    const blocks = Math.max(1, Math.min(22, Math.round((pct / 100) * 22)));

    return (
        <div className="loading-root">
            <style>{`
                .loading-root {
                    position: fixed;
                    inset: 0;
                    width: 100vw;
                    height: 100vh;
                    min-height: 100dvh;
                    z-index: 99999;
                    overflow: hidden;
                    display: grid;
                    place-items: center;
                    background:
                        radial-gradient(circle at 72% 28%, rgba(132, 214, 199, 0.13), transparent 26%),
                        radial-gradient(circle at 18% 76%, rgba(212, 113, 68, 0.13), transparent 28%),
                        linear-gradient(180deg, #08060f 0%, #101314 100%);
                    color: #f4efe8;
                    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
                }

                .loading-root::before {
                    content: "";
                    position: absolute;
                    inset: 0;
                    background:
                        repeating-linear-gradient(0deg, rgba(255,255,255,0.03) 0 1px, transparent 1px 5px),
                        repeating-linear-gradient(90deg, rgba(255,255,255,0.018) 0 1px, transparent 1px 12px);
                    pointer-events: none;
                    opacity: 0.55;
                }

                .loading-shell {
                    position: relative;
                    width: min(1040px, calc(100vw - 56px));
                    min-height: min(620px, calc(100dvh - 56px));
                    display: grid;
                    grid-template-rows: 1fr auto;
                    gap: clamp(26px, 5vh, 46px);
                    padding: clamp(30px, 5vw, 58px);
                    box-sizing: border-box;
                }

                .loading-main {
                    display: grid;
                    grid-template-columns: minmax(0, 1fr) minmax(260px, 380px);
                    gap: clamp(36px, 7vw, 82px);
                    align-items: center;
                }

                .loading-kicker {
                    color: #84d6c7;
                    font-size: 13px;
                    letter-spacing: 0.16em;
                    text-transform: uppercase;
                    margin-bottom: 18px;
                }

                .loading-title {
                    margin: 0;
                    max-width: 660px;
                    color: #fffaf2;
                    font-size: clamp(40px, 7.6vw, 92px);
                    line-height: 0.92;
                    letter-spacing: 0;
                    text-transform: uppercase;
                    text-shadow:
                        4px 0 0 #08060f,
                        8px 7px 0 #d47144;
                }

                .loading-copy {
                    margin-top: 24px;
                    max-width: 620px;
                    color: #d7d1ca;
                    font-size: clamp(14px, 1.55vw, 17px);
                    line-height: 1.55;
                }

                .loading-log {
                    margin-top: 34px;
                    display: grid;
                    gap: 10px;
                    color: #b8eadf;
                    font-size: 14px;
                    max-width: 520px;
                }

                .loading-log span {
                    opacity: 0;
                    animation: bootLine 900ms steps(2, end) forwards;
                }

                .loading-log span::before {
                    content: "$ ";
                    color: #d47144;
                }

                .butterfly-stage {
                    min-height: 330px;
                    display: grid;
                    place-items: center;
                    position: relative;
                }

                .butterfly {
                    position: relative;
                    width: 280px;
                    height: 250px;
                    --butterfly-scale: 1;
                    transform: scale(var(--butterfly-scale));
                    animation: butterflyFloat 2400ms steps(8, end) infinite;
                    image-rendering: pixelated;
                }

                .loading-butterfly-canvas {
                    display: block;
                    width: 100%;
                    height: 100%;
                    image-rendering: pixelated;
                    image-rendering: crisp-edges;
                }

                .wing {
                    position: absolute;
                    top: 46px;
                    width: 94px;
                    height: 118px;
                    background:
                        linear-gradient(90deg, rgba(255,255,255,0.18), transparent 38%),
                        #66addd;
                    box-shadow:
                        0 -18px 0 0 #76bee9,
                        18px -28px 0 -2px #8bd1f0,
                        -16px 18px 0 0 #5ca2d4,
                        12px 58px 0 -6px #87cced,
                        34px 46px 0 -18px #d8f2ff,
                        20px 88px 0 -20px #e8f8ff,
                        42px 76px 0 -18px #3f84bc,
                        0 0 0 9px rgba(4, 7, 12, 0.62),
                        12px 14px 0 11px rgba(255,255,255,0.06);
                    transform-origin: center right;
                }

                .wing.left {
                    left: 34px;
                    clip-path: polygon(24% 0, 76% 0, 100% 18%, 100% 74%, 78% 92%, 72% 100%, 24% 100%, 24% 80%, 0 80%, 0 30%, 18% 30%);
                    animation: wingLeft 760ms steps(2, end) infinite;
                }

                .wing.right {
                    right: 34px;
                    clip-path: polygon(24% 0, 76% 0, 82% 30%, 100% 30%, 100% 80%, 76% 80%, 76% 100%, 28% 100%, 22% 92%, 0 74%, 0 18%);
                    transform-origin: center left;
                    animation: wingRight 760ms steps(2, end) infinite;
                }

                .butterfly-body {
                    position: absolute;
                    left: 50%;
                    top: 62px;
                    width: 22px;
                    height: 116px;
                    transform: translateX(-50%);
                    background: #234d73;
                    box-shadow:
                        0 -22px 0 -5px #2e5b83,
                        0 0 0 7px rgba(4, 7, 12, 0.64),
                        0 44px 0 -6px #1f4164,
                        0 76px 0 -7px #1a3759;
                }

                .butterfly-body::before,
                .butterfly-body::after {
                    content: "";
                    position: absolute;
                    top: -40px;
                    width: 54px;
                    height: 22px;
                    border-top: 10px solid #5e9fd1;
                }

                .butterfly-body::before {
                    right: 8px;
                    transform: rotate(-32deg);
                }

                .butterfly-body::after {
                    left: 8px;
                    transform: rotate(32deg);
                }

                .spark {
                    position: absolute;
                    width: 8px;
                    height: 8px;
                    background: #f0c27b;
                    opacity: 0;
                    animation: sparkle 1500ms steps(3, end) infinite;
                }

                .spark.one { left: 16px; top: 28px; animation-delay: 120ms; }
                .spark.two { right: 22px; top: 58px; animation-delay: 420ms; }
                .spark.three { left: 56px; bottom: 18px; animation-delay: 760ms; }

                .loading-progress {
                    max-width: 720px;
                }

                .loading-progress__meta {
                    display: flex;
                    justify-content: space-between;
                    gap: 18px;
                    color: #f4efe8;
                    font-size: 13px;
                    margin-bottom: 10px;
                }

                .loading-progress__bar {
                    height: 18px;
                    border: 2px solid #f4efe8;
                    background: rgba(0, 0, 0, 0.34);
                    padding: 2px;
                    overflow: hidden;
                }

                .loading-progress__fill {
                    height: 100%;
                    width: var(--progress);
                    background:
                        repeating-linear-gradient(90deg, #84d6c7 0 12px, #5fb9ad 12px 14px);
                    transition: width 180ms steps(4, end);
                }

                .loading-progress__blocks {
                    margin-top: 9px;
                    color: #84d6c7;
                    font-size: 13px;
                    letter-spacing: 2px;
                    white-space: nowrap;
                }

                @keyframes bootLine {
                    to { opacity: 1; }
                }

                @keyframes butterflyFloat {
                    50% { transform: translateY(-12px) scale(var(--butterfly-scale)); }
                }

                @keyframes wingLeft {
                    50% { transform: translateX(10px) scaleX(0.72); }
                }

                @keyframes wingRight {
                    50% { transform: translateX(-10px) scaleX(0.72); }
                }

                @keyframes sparkle {
                    0%, 100% { opacity: 0; transform: translateY(0); }
                    45% { opacity: 1; transform: translateY(-8px); }
                }

                @media (max-width: 820px) {
                    .loading-shell {
                        width: min(680px, calc(100vw - 32px));
                        min-height: calc(100dvh - 32px);
                    }

                    .loading-main {
                        grid-template-columns: 1fr;
                        gap: 28px;
                    }

                    .butterfly-stage {
                        min-height: 210px;
                    }

                    .butterfly {
                        --butterfly-scale: 0.78;
                    }
                }
            `}</style>

            <main className="loading-shell">
                <section className="loading-main">
                    <div>
                        <p className="loading-kicker">boot sequence / personal site</p>
                        <h1 className="loading-title">
                            Welcome
                            <br />
                            to Zhou Xu&apos;s
                            <br />
                            portfolio
                        </h1>
                        <p className="loading-copy">
                            Initializing a retro desktop of academic papers, product design
                            projects, awards, AI work and personal experiments.
                        </p>
                        <div className="loading-log">
                            {bootLines.map((line, index) => (
                                <span key={line} style={{ animationDelay: `${index * 150}ms` }}>
                                    {line}
                                </span>
                            ))}
                        </div>
                    </div>

                    <aside className="butterfly-stage" aria-label="Pixel butterfly companion">
                        <div className="butterfly">
                            <LoadingButterfly />
                        </div>
                    </aside>
                </section>

                <section className="loading-progress" aria-label="Loading progress">
                    <div className="loading-progress__meta">
                        <span>loading assets</span>
                        <span>{pct}%</span>
                    </div>
                    <div className="loading-progress__bar">
                        <div
                            className="loading-progress__fill"
                            style={{ ['--progress' as string]: `${pct}%` }}
                        />
                    </div>
                    <div className="loading-progress__blocks">
                        {'█'.repeat(blocks)}
                        {'░'.repeat(22 - blocks)}
                    </div>
                </section>
            </main>
        </div>
    );
};

export default LoadingScreen;
